import type { IBookingCalendarPassengerDto } from "../dto/bookings/booking.dto";
import { PassengerEligibleEnum } from "../enums/passenger-eligible.enum";
import {
  DEFAULT_FIELD_ORDER,
  type FieldConfigType,
  type FieldKey,
} from "../models/booking-calendar.column-passengers.models";
import { formatPhoneLocal } from "./phone.helpers";

const ELIGIBLE_TRANSLATION_KEYS: Record<PassengerEligibleEnum, string> = {
  [PassengerEligibleEnum.eligible]: "passenger.gridLabel.Eligible",
  [PassengerEligibleEnum.notEligible]: "passenger.gridLabel.Not Eligible",
  [PassengerEligibleEnum.eligibleWithLimitations]:
    "passenger.gridLabel.Eligible With Limitations",
} as const;

export const buildFieldConfig = (
  passengerCellData: string[] | undefined,
  t?: (key: string) => string,
): {
  fieldConfig: Record<FieldKey, FieldConfigType>;
  orderedFieldKeys: string[];
} => {
  const normalizedPassengerCellData = passengerCellData?.length
    ? passengerCellData
    : (DEFAULT_FIELD_ORDER as unknown as string[]);

  const fieldConfig: Record<FieldKey, FieldConfigType> = {
    code: {
      show: normalizedPassengerCellData.includes("code"),
      className: "code",
      getValue: (passenger: IBookingCalendarPassengerDto | null) =>
        passenger?.code,
    },
    phone: {
      show: normalizedPassengerCellData.includes("phone"),
      className: "mobile",
      getValue: (passenger: IBookingCalendarPassengerDto | null) =>
        formatPhoneLocal(passenger?.phoneMobile),
    },
    unit: {
      show: normalizedPassengerCellData.includes("unit"),
      className: "unit",
      getValue: (passenger: IBookingCalendarPassengerDto | null) =>
        passenger?.unitName,
    },
    area: {
      show: normalizedPassengerCellData.includes("area"),
      className: "area",
      getValue: (passenger: IBookingCalendarPassengerDto | null) =>
        passenger?.fromStation2AreaName || passenger?.fromStation1AreaName,
    },
    active: {
      excludeFromMapList: true,
      show: normalizedPassengerCellData.includes("active"),
      className: "active",
      getValue: (passenger: IBookingCalendarPassengerDto | null) => {
        if (passenger?.isActive === undefined) {
          return "";
        }
        return passenger.isActive
          ? t?.("passenger.gridLabel.Active")
          : t?.("passenger.gridLabel.Not Active");
      },
    },
    eligible: {
      show: normalizedPassengerCellData.includes("eligible"),
      className: "eligible",
      getValue: (passenger: IBookingCalendarPassengerDto | null) => {
        if (!passenger?.eligible) {
          return "";
        }
        return t?.(ELIGIBLE_TRANSLATION_KEYS[passenger.eligible]);
      },
    },
    passengerType: {
      show: normalizedPassengerCellData.includes("passengerType"),
      className: "passenger-type",
      getValue: (passenger: IBookingCalendarPassengerDto | null) =>
        passenger?.passengerTypeName,
    },
    referent: {
      show: normalizedPassengerCellData.includes("referent"),
      className: "referent",
      getValue: (passenger: IBookingCalendarPassengerDto | null) =>
        passenger?.referent,
    },
  } as const;

  const orderedFieldKeys = [...normalizedPassengerCellData];

  return { fieldConfig, orderedFieldKeys };
};
